import React, { useState, useEffect, useRef } from 'react';
import { GetAll, apiURL } from '../../utils/apiCalls';

// Cache for banner ads data
const bannerAdsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Video Banner Carousel Component
 * Main header carousel that displays ads in a rotating carousel format
 * Similar to the hero carousel but populated from database ads
 */
const VideoBannerCarousel = ({ positionId, autoRotateInterval = 3000 }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const trackRef = useRef(null);
  const dotsContainerRef = useRef(null);

  /**
   * Get session identifier for personalized ad selection
   * Since ads are displayed on public pages (before login), we always use session ID
   * 
   * This ensures:
   * - Different browser sessions see different ads
   * - Same browser session sees consistent ads (on page reload)
   * - Each user/browser gets personalized ad selection
   * 
   * Session ID persists across page reloads but is unique per browser
   */
  const getUserIdentifier = () => {
    // Always use session ID since ads are shown before login
    // Get existing session ID or generate a new one
    let sessionId = sessionStorage.getItem('banner_ads_session_id');
    
    if (!sessionId) {
      // Generate a unique session ID for this browser session
      // This persists across page reloads but is unique per browser
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('banner_ads_session_id', sessionId);
    }
    
    return sessionId;
  };

  useEffect(() => {
    /**
     * Simple seeded random number generator
     * Uses a seed to generate consistent "random" numbers for the same seed
     * Different seeds produce different sequences
     */
    const seededRandom = (seed) => {
      let value = seed;
      return () => {
        value = (value * 9301 + 49297) % 233280;
        return value / 233280;
      };
    };

    /**
     * Hash string to numeric seed
     */
    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash);
    };

    /**
     * Seeded Fisher-Yates shuffle algorithm for user-specific ad ordering
     * Uses user identifier to generate different shuffles for different users
     */
    const shuffleArrayWithSeed = (array, seed) => {
      const shuffled = [...array]; // Create a copy to avoid mutating original
      const random = seededRandom(seed);
      
      for (let i = shuffled.length - 1; i > 0; i--) {
        // Use seeded random instead of Math.random()
        const j = Math.floor(random() * (i + 1));
        // Swap elements at positions i and j
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    const fetchAds = async () => {
      if (!positionId) {
        setLoading(false);
        return;
      }

      // Check cache first
      const cacheKey = `banner_position_${positionId}`;
      const cached = bannerAdsCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        // Apply user-specific shuffle to cached ads
        const userIdentifier = getUserIdentifier();
        const baseSeed = hashString(userIdentifier);
        const shuffledAds = shuffleArrayWithSeed(cached.data, baseSeed);
        setAds(shuffledAds);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await GetAll(apiURL + '/api/v1/ads');
        
        if (response && response.data && response.data.status) {
          const allAds = response.data.data || [];
          const filteredAds = allAds.filter(ad => String(ad.position_id) === String(positionId));
          
          if (filteredAds.length === 0) {
            setLoading(false);
            bannerAdsCache.set(cacheKey, { data: [], timestamp: Date.now() });
            return;
          }
          
          // Fetch uploads for all ads in parallel
          const uploadPromises = filteredAds.map(async (ad) => {
            try {
              const assetsResponse = await GetAll(apiURL + `/api/v1/ads/${ad.id}/uploads`);
              const uploads = assetsResponse?.data?.data || [];
              const assets = Array.isArray(uploads) ? uploads : (uploads.toJSON ? uploads.toJSON() : []);
              
              // Filter to get valid assets (completed or uploading with URLs)
              const validAssets = assets.filter(asset => {
                if (!asset.filepaths3) return false;
                if (asset.hasOwnProperty('status')) {
                  return asset.status === 'completed' || asset.status === 'uploading';
                }
                return true; // If no status field, show all
              });
              
              return { ...ad, assets: validAssets };
            } catch (err) {
              console.error(`Error fetching uploads for ad ${ad.id}:`, err);
              return { ...ad, assets: [] };
            }
          });
          
          const results = await Promise.allSettled(uploadPromises);
          const adsWithAssets = results
            .filter(result => result.status === 'fulfilled')
            .map(result => result.value)
            .filter(ad => ad.assets && ad.assets.length > 0);
          
          // Cache the results (without shuffle)
          bannerAdsCache.set(cacheKey, { data: adsWithAssets, timestamp: Date.now() });
          
          // Apply user-specific shuffle before setting ads
          // This ensures different users see ads in different order
          const userIdentifier = getUserIdentifier();
          const baseSeed = hashString(userIdentifier);
          const shuffledAds = shuffleArrayWithSeed(adsWithAssets, baseSeed);
          
          console.log(`VideoBannerCarousel - Shuffled ads for ${userIdentifier}: ${shuffledAds.length} ads`);
          setAds(shuffledAds);
        }
      } catch (error) {
        console.error('Error fetching banner ads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [positionId]);

  // Initialize carousel dots
  useEffect(() => {
    if (ads.length === 0 || !dotsContainerRef.current) return;

    // Clear existing dots
    dotsContainerRef.current.innerHTML = '';

    // Create dots
    for (let i = 0; i < ads.length; i++) {
      const dot = document.createElement('div');
      dot.className = 'dot' + (i === 0 ? ' active' : '');
      dotsContainerRef.current.appendChild(dot);
    }
  }, [ads]);

  // Auto-rotate carousel
  useEffect(() => {
    if (ads.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [ads.length, autoRotateInterval]);

  // Update carousel position and dots
  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(-${currentIndex * 100}%)`;
    }

    // Update dots
    if (dotsContainerRef.current) {
      const dots = dotsContainerRef.current.children;
      for (let i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('active', i === currentIndex);
      }
    }
  }, [currentIndex]);

  if (loading || ads.length === 0) {
    return null;
  }

  const renderAdCard = (ad, index) => {
    const asset = ad?.assets?.[0];
    if (!asset || !asset.filepaths3) return null;

    const fileExt = asset.file_ext?.toLowerCase() || '';
    const fileName = asset.file_name?.toLowerCase() || '';
    const isVideo = /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(fileExt) || 
                    /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(fileName);
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileExt) || 
                    /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);

    const handleClick = () => {
      if (ad.link_url) {
        window.open(ad.link_url, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <div 
        className="carousel-card" 
        key={ad.id || index}
        style={{ cursor: ad.link_url ? 'pointer' : 'default' }}
        onClick={handleClick}
      >
        {isVideo ? (
          <video
            className="carousel-bg"
            src={asset.filepaths3}
            muted
            autoPlay
            loop
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        ) : isImage ? (
          <img
            className="carousel-bg"
            src={asset.filepaths3}
            alt={ad.name || 'Advertisement'}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : null}
        {/* {(ad.name || ad.description) && (
          <div className="carousel-overlay" style={{ display: 'block', visibility: 'visible' }}>
            {ad.name && <h2>{ad.name}</h2>}
            {ad.description && (
              <p style={{ 
                display: 'block', 
                visibility: 'visible', 
                opacity: 1,
                lineHeight: '1.6',
                marginTop: ad.name ? '8px' : '0',
                fontSize: '17px',
                color: '#ffffff',
                textShadow: '0 1px 5px rgba(0,0,0,0.5)'
              }}>
                {ad.description}
              </p>
            )}
          </div>
        )} */}
      </div>
    );
  };

  return (
    <section className="hero-carousel">
      <div className="carousel-track" ref={trackRef} id="carouselTrack">
        {ads.map((ad, index) => renderAdCard(ad, index))}
      </div>
      <div className="carousel-dots" ref={dotsContainerRef} id="carouselDots"></div>
    </section>
  );
};

export default VideoBannerCarousel;

